import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Copy, Check, MessageCircle, Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = ["Orange Money (Max It)", "M-Pesa", "Airtel Money", "Illico Cash"];

export default function MobileMoney() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [reg, setReg] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const { t } = useLang();

  useEffect(() => {
    if (!id) { setError(true); return; }
    axios.get(`${API}/registrations/${id}`).then((r) => setReg(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col items-center justify-center gap-6">
        <p className="text-[#71717A]">{t("mm.notfound")}</p>
        <Link to="/" className="text-[#C5A059] text-xs uppercase tracking-[0.25em] border border-[#C5A059] px-6 py-3">{t("mm.back")}</Link>
      </div>
    );
  }
  if (!reg) {
    return <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center"><p className="text-[#71717A] text-xs uppercase tracking-[0.3em]">Chargement…</p></div>;
  }

  const copyRef = () => {
    navigator.clipboard.writeText(reg.reference);
    setCopied(true);
    toast.success(t("mm.copy"));
    setTimeout(() => setCopied(false), 2000);
  };

  const waText = encodeURIComponent(`Bonjour D2R, voici ma preuve de paiement Mobile Money pour l'inscription détection. Référence : ${reg.reference} — ${reg.prenom} ${reg.nom}`);

  const uploadPreuve = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/registrations/${id}/preuve`, fd);
      toast.success(t("mm.toast.ok"), { description: t("mm.toast.ok.x") });
      setReg({ ...reg, preuve_paiement: "ok" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reg.toast.error"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center px-6 py-16">
      <div className="max-w-xl w-full border border-[#27272A] bg-[#121214] p-10 lg:p-12" data-testid="mobile-money-page">
        <img src="/assets/logo-dark-sm.png" alt="D2R" className="h-12 w-auto mb-10" />
        <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-4">{t("mm.eyebrow")}</p>
        <h1 className="font-display text-3xl uppercase font-bold leading-tight">
          {t("mm.title.a")} <span className="text-[#C5A059]">{reg.prenom}</span>
        </h1>

        <div className="mt-8 border border-[#C5A059]/40 bg-[#0A0A0C] p-6 flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{t("mm.ref")}</p>
            <p className="font-display text-2xl text-[#C5A059] font-medium mt-1" data-testid="mobile-money-reference">{reg.reference}</p>
          </div>
          <button onClick={copyRef} data-testid="mobile-money-copy" className="text-[#71717A] hover:text-[#C5A059] transition-colors p-2" aria-label="Copier">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </button>
        </div>

        <ol className="mt-8 space-y-4 text-sm text-[#A1A1AA] leading-relaxed list-none">
          <li><span className="text-[#C5A059] font-semibold">1.</span> {t("mm.s1")}</li>
        </ol>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {METHODS.map((m) => (
            <div key={m} className="border border-[#27272A] px-4 py-3 text-center text-xs uppercase tracking-wider text-white" data-testid={`mm-method-${m.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              {m}
            </div>
          ))}
        </div>
        <p className="text-sm text-[#A1A1AA] mt-6 leading-relaxed">
          <span className="text-[#C5A059] font-semibold">2.</span> {t("mm.s2")} <strong className="text-white">{reg.reference}</strong>
        </p>
        <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">
          <span className="text-[#C5A059] font-semibold">3.</span> {t("mm.s3")}
        </p>

        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPreuve} data-testid="mobile-money-file-input" />
        {reg.preuve_paiement ? (
          <div className="mt-8 flex items-center justify-center gap-3 border border-[#C5A059]/40 text-[#C5A059] px-8 py-4 text-xs uppercase tracking-[0.25em]" data-testid="mobile-money-proof-sent">
            <CheckCircle2 size={16} /> {t("mm.uploaded")}
          </div>
        ) : (
          <button
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            data-testid="mobile-money-upload"
            className="mt-8 w-full flex items-center justify-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {t("mm.upload")}
          </button>
        )}

        <a
          href={`https://wa.me/243846846760?text=${waText}`}
          target="_blank"
          rel="noreferrer"
          data-testid="mobile-money-whatsapp"
          className="mt-4 flex items-center justify-center gap-3 border border-[#C5A059] text-[#C5A059] px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#C5A059] hover:text-black transition-colors duration-300"
        >
          <MessageCircle size={16} /> {t("mm.whatsapp")}
        </a>
        <Link to="/" className="block text-center text-[#71717A] text-xs mt-6 hover:text-[#C5A059] transition-colors">← {t("pay.back")}</Link>
      </div>
    </div>
  );
}
